import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  listEmployeeActivities, createEmployeeActivity, updateEmployeeActivity, deleteEmployeeActivity,
  fetchMyActivities, fetchActivitySummary,
  listDailyStandups, createDailyStandup, updateDailyStandup, reviewDailyStandup, deleteDailyStandup,
} from '../api';
import { useConfirm } from './ConfirmProvider';

const CATEGORY_OPTIONS = [
  { value: 'BILLABLE_WORK', label: 'Billable Work' },
  { value: 'NON_BILLABLE_WORK', label: 'Non-Billable Work' },
  { value: 'ADMIN', label: 'Administration' },
  { value: 'TRAINING', label: 'Training & Development' },
  { value: 'MEETING', label: 'Meeting' },
  { value: 'MENTORING', label: 'Mentoring' },
  { value: 'BUSINESS_DEV', label: 'Business Development' },
  { value: 'INTERNAL_PROJECT', label: 'Internal Project' },
  { value: 'PRO_BONO', label: 'Pro Bono' },
  { value: 'COURT_APPEARANCE', label: 'Court Appearance' },
  { value: 'CLIENT_COMMS', label: 'Client Communication' },
  { value: 'RESEARCH', label: 'Research' },
  { value: 'TRAVEL', label: 'Travel' },
  { value: 'BREAK', label: 'Break' },
  { value: 'OTHER', label: 'Other' },
];

const MOOD_OPTIONS = [
  { value: 'GREAT', label: '😊 Great' },
  { value: 'GOOD', label: '🙂 Good' },
  { value: 'NEUTRAL', label: '😐 Neutral' },
  { value: 'STRUGGLING', label: '😟 Struggling' },
];

const CATEGORY_COLORS = {
  BILLABLE_WORK: 'bg-green-100 text-green-700',
  NON_BILLABLE_WORK: 'bg-yellow-100 text-yellow-700',
  ADMIN: 'bg-gray-100 text-gray-700',
  TRAINING: 'bg-blue-100 text-blue-700',
  MEETING: 'bg-purple-100 text-purple-700',
  MENTORING: 'bg-indigo-100 text-indigo-700',
  BUSINESS_DEV: 'bg-teal-100 text-teal-700',
  INTERNAL_PROJECT: 'bg-cyan-100 text-cyan-700',
  PRO_BONO: 'bg-pink-100 text-pink-700',
  COURT_APPEARANCE: 'bg-red-100 text-red-700',
  CLIENT_COMMS: 'bg-amber-100 text-amber-700',
  RESEARCH: 'bg-sky-100 text-sky-700',
  TRAVEL: 'bg-orange-100 text-orange-700',
  BREAK: 'bg-slate-100 text-slate-600',
  OTHER: 'bg-gray-100 text-gray-600',
};

function ActivityLog({ authAxios, showFormMessage, userRole, currentUser }) {
  const confirm = useConfirm();
  const [activeSubTab, setActiveSubTab] = useState('activities');
  const [activities, setActivities] = useState([]);
  const [standups, setStandups] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Filters
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('my'); // 'my' or 'team'

  // Forms
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [showStandupForm, setShowStandupForm] = useState(false);
  const [editingStandup, setEditingStandup] = useState(null);

  const [activityForm, setActivityForm] = useState({
    date: new Date().toISOString().slice(0, 10), category: 'OTHER', description: '', hours: '', case: '', task: '',
  });
  const [standupForm, setStandupForm] = useState({
    date: new Date().toISOString().slice(0, 10), accomplishments: '', blockers: '', plan_for_tomorrow: '', mood: 'GOOD',
  });
  const [reviewNotesMap, setReviewNotesMap] = useState({});

  const isManager = useMemo(() =>
    ['PARTNER', 'SUPERUSER'].includes(userRole) || currentUser?.is_superuser,
    [userRole, currentUser]
  );

  /* ---- Data fetching ---- */
  const fetchActivities = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_after = dateFrom;
      if (dateTo) params.date_before = dateTo;
      if (categoryFilter) params.category = categoryFilter;
      if (searchTerm) params.search = searchTerm;

      let data;
      if (viewMode === 'team' && isManager) {
        data = await listEmployeeActivities(authAxios, params);
      } else {
        data = await fetchMyActivities(authAxios, params);
      }
      setActivities(Array.isArray(data) ? data : []);
    } catch { showFormMessage('error', 'Failed to load activities.'); }
    finally { setIsLoading(false); }
  }, [authAxios, showFormMessage, dateFrom, dateTo, categoryFilter, searchTerm, viewMode, isManager]);

  const fetchStandups = useCallback(async () => {
    try {
      const params = {};
      if (dateFrom) params.date_after = dateFrom;
      if (dateTo) params.date_before = dateTo;
      const data = await listDailyStandups(authAxios, params);
      setStandups(Array.isArray(data) ? data : []);
    } catch { showFormMessage('error', 'Failed to load standups.'); }
  }, [authAxios, showFormMessage, dateFrom, dateTo]);

  const fetchSummaryData = useCallback(async () => {
    try {
      const params = {};
      if (dateFrom) params.date_after = dateFrom;
      if (dateTo) params.date_before = dateTo;
      const data = await fetchActivitySummary(authAxios, params);
      setSummary(data);
    } catch {}
  }, [authAxios, dateFrom, dateTo]);

  useEffect(() => {
    fetchActivities();
    fetchStandups();
    fetchSummaryData();
  }, [fetchActivities, fetchStandups, fetchSummaryData]);

  /* ---- Activity CRUD ---- */
  const resetActivityForm = () => {
    setActivityForm({ date: new Date().toISOString().slice(0, 10), category: 'OTHER', description: '', hours: '', case: '', task: '' });
    setEditingActivity(null);
    setShowActivityForm(false);
  };

  const handleActivitySubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...activityForm };
      if (!payload.case) delete payload.case;
      if (!payload.task) delete payload.task;
      if (editingActivity) {
        await updateEmployeeActivity(authAxios, editingActivity.id, payload);
        showFormMessage('success', 'Activity updated.');
      } else {
        await createEmployeeActivity(authAxios, payload);
        showFormMessage('success', 'Activity logged.');
      }
      resetActivityForm();
      fetchActivities();
      fetchSummaryData();
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Failed to save activity.');
    }
  };

  const handleEditActivity = (a) => {
    setActivityForm({
      date: a.date, category: a.category, description: a.description,
      hours: a.hours, case: a.case || '', task: a.task || '',
    });
    setEditingActivity(a);
    setShowActivityForm(true);
  };

  const handleDeleteActivity = async (id) => {
    if (!(await confirm({ title: 'Delete Activity', message: 'Delete this activity entry?', confirmText: 'Delete', variant: 'danger' }))) return;
    try {
      await deleteEmployeeActivity(authAxios, id);
      showFormMessage('success', 'Activity deleted.');
      fetchActivities();
    } catch { showFormMessage('error', 'Failed to delete activity.'); }
  };

  /* ---- Standup CRUD ---- */
  const resetStandupForm = () => {
    setStandupForm({ date: new Date().toISOString().slice(0, 10), accomplishments: '', blockers: '', plan_for_tomorrow: '', mood: 'GOOD' });
    setEditingStandup(null);
    setShowStandupForm(false);
  };

  const handleStandupSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingStandup) {
        await updateDailyStandup(authAxios, editingStandup.id, standupForm);
        showFormMessage('success', 'Standup updated.');
      } else {
        await createDailyStandup(authAxios, standupForm);
        showFormMessage('success', 'Standup submitted.');
      }
      resetStandupForm();
      fetchStandups();
    } catch (err) {
      const detail = err.response?.data?.detail || err.response?.data?.non_field_errors?.[0] || 'Failed to submit standup.';
      showFormMessage('error', detail);
    }
  };

  const handleReviewStandup = async (id) => {
    try {
      await reviewDailyStandup(authAxios, id, { manager_notes: reviewNotesMap[id] || '' });
      showFormMessage('success', 'Standup reviewed.');
      fetchStandups();
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Failed to review standup.');
    }
  };

  const handleEditStandup = (s) => {
    setStandupForm({
      date: s.date, accomplishments: s.accomplishments || '',
      blockers: s.blockers || '', plan_for_tomorrow: s.plan_for_tomorrow || '',
      mood: s.mood || 'GOOD',
    });
    setEditingStandup(s);
    setShowStandupForm(true);
  };

  const handleDeleteStandup = async (id) => {
    if (!(await confirm({ title: 'Delete Standup', message: 'Delete this standup entry?', confirmText: 'Delete', variant: 'danger' }))) return;
    try {
      await deleteDailyStandup(authAxios, id);
      showFormMessage('success', 'Standup deleted.');
      fetchStandups();
    } catch { showFormMessage('error', 'Failed to delete standup.'); }
  };

  /* ---- Group activities by date ---- */
  const groupedActivities = useMemo(() => {
    const grouped = {};
    activities.forEach(a => {
      if (!grouped[a.date]) grouped[a.date] = [];
      grouped[a.date].push(a);
    });
    return Object.entries(grouped).sort((a, b) => b[0].localeCompare(a[0]));
  }, [activities]);

  /* ---- Summary stats ---- */
  const totalHours = useMemo(() => activities.reduce((sum, a) => sum + parseFloat(a.hours || 0), 0), [activities]);
  const billableHours = useMemo(() =>
    activities.filter(a => a.category === 'BILLABLE_WORK').reduce((sum, a) => sum + parseFloat(a.hours || 0), 0),
    [activities]
  );

  const subTabs = [
    { id: 'activities', label: 'Activities' },
    { id: 'standups', label: 'Daily Standups' },
    { id: 'summary', label: 'Summary' },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Activity Log</h2>
          <p className="text-sm text-gray-500 mt-1">Track daily activities... and submit standups</p>
        </div>
        <div className="flex gap-2">
          {activeSubTab === 'activities' && (
            <button onClick={() => { resetActivityForm(); setShowActivityForm(true); }}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition">
              + Log Activity
            </button>
          )}
          {activeSubTab === 'standups' && (
            <button onClick={() => { resetStandupForm(); setShowStandupForm(true); }}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition">
              + Daily Standup
            </button>
          )}
        </div>
      </div>

      {/* Sub-tab navigation */}
      <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1 w-fit">
        {subTabs.map(t => (
          <button key={t.id} onClick={() => setActiveSubTab(t.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition ${activeSubTab === t.id ? 'bg-white shadow text-blue-700' : 'text-gray-600 hover:text-gray-800'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border p-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm w-full" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm w-full" />
          </div>
          {activeSubTab === 'activities' && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Category</label>
              <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm w-full">
                <option value="">All Categories</option>
                {CATEGORY_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search descriptions..." className="border rounded-lg px-3 py-2 text-sm w-full" />
          </div>
          {isManager && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">View</label>
              <select value={viewMode} onChange={e => setViewMode(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm w-full">
                <option value="my">My Activities</option>
                <option value="team">Team Activities</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Quick stats */}
      {activeSubTab === 'activities' && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div className="bg-white rounded-xl shadow-sm border p-4 text-center">
            <p className="text-2xl font-bold text-gray-800">{totalHours.toFixed(1)}</p>
            <p className="text-xs text-gray-500">Total Hours</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{billableHours.toFixed(1)}</p>
            <p className="text-xs text-gray-500">Billable Hours</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{activities.length}</p>
            <p className="text-xs text-gray-500">Entries</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4 text-center">
            <p className="text-2xl font-bold text-purple-600">
              {totalHours > 0 ? ((billableHours / totalHours) * 100).toFixed(0) : 0}%
            </p>
            <p className="text-xs text-gray-500">Utilization</p>
          </div>
        </div>
      )}

      {isLoading && <div className="text-center py-8 text-gray-500">Loading...</div>}

      {/* ==================== ACTIVITIES TAB ==================== */}
      {activeSubTab === 'activities' && !isLoading && (
        <>
          {/* Activity Form Modal */}
          {showActivityForm && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <h3 className="text-lg font-semibold mb-4">{editingActivity ? 'Edit Activity' : 'Log Activity'}</h3>
                  <form onSubmit={handleActivitySubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                        <input type="date" required value={activityForm.date}
                          onChange={e => setActivityForm(p => ({ ...p, date: e.target.value }))}
                          className="border rounded-lg px-3 py-2 text-sm w-full" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Hours *</label>
                        <input type="number" required step="0.25" min="0" max="24" value={activityForm.hours}
                          onChange={e => setActivityForm(p => ({ ...p, hours: e.target.value }))}
                          className="border rounded-lg px-3 py-2 text-sm w-full" placeholder="0.00" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                      <select required value={activityForm.category}
                        onChange={e => setActivityForm(p => ({ ...p, category: e.target.value }))}
                        className="border rounded-lg px-3 py-2 text-sm w-full">
                        {CATEGORY_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea rows={3} value={activityForm.description}
                        onChange={e => setActivityForm(p => ({ ...p, description: e.target.value }))}
                        className="border rounded-lg px-3 py-2 text-sm w-full" placeholder="What did you work on?" />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <button type="button" onClick={resetActivityForm}
                        className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
                      <button type="submit"
                        className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                        {editingActivity ? 'Update' : 'Log Activity'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* Activities grouped by date */}
          {groupedActivities.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border p-8 text-center text-gray-500">
              No activities found for the selected period.
            </div>
          ) : (
            groupedActivities.map(([date, items]) => (
              <div key={date} className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="text-sm font-semibold text-gray-600">
                    {new Date(date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' })}
                  </h4>
                  <span className="text-xs text-gray-400">
                    ({items.reduce((s, a) => s + parseFloat(a.hours || 0), 0).toFixed(1)}h)
                  </span>
                </div>
                <div className="bg-white rounded-xl shadow-sm border divide-y">
                  {items.map(a => (
                    <div key={a.id} className="p-4 flex items-start gap-3 hover:bg-gray-50 transition">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${CATEGORY_COLORS[a.category] || CATEGORY_COLORS.OTHER}`}>
                            {a.category_display || a.category}
                          </span>
                          {viewMode === 'team' && a.user_full_name && (
                            <span className="text-xs text-gray-500 font-medium">{a.user_full_name}</span>
                          )}
                          {a.source === 'AUTO_TIME_ENTRY' && (
                            <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">Auto</span>
                          )}
                          {a.case_title && (
                            <span className="text-xs text-gray-400">📂 {a.case_title}</span>
                          )}
                        </div>
                        {a.description && (
                          <p className="text-sm text-gray-700 mt-1 line-clamp-2">{a.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-sm font-semibold text-gray-700">{parseFloat(a.hours).toFixed(1)}h</span>
                        {a.source !== 'AUTO_TIME_ENTRY' && (
                          <div className="flex gap-1">
                            <button onClick={() => handleEditActivity(a)}
                              className="text-gray-400 hover:text-blue-600 text-xs" title="Edit">✏️</button>
                            <button onClick={() => handleDeleteActivity(a.id)}
                              className="text-gray-400 hover:text-red-600 text-xs" title="Delete">🗑️</button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </>
      )}

      {/* ==================== STANDUPS TAB ==================== */}
      {activeSubTab === 'standups' && !isLoading && (
        <>
          {showStandupForm && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <h3 className="text-lg font-semibold mb-4">{editingStandup ? 'Edit Standup' : 'Daily Standup'}</h3>
                  <form onSubmit={handleStandupSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                        <input type="date" required value={standupForm.date}
                          onChange={e => setStandupForm(p => ({ ...p, date: e.target.value }))}
                          className="border rounded-lg px-3 py-2 text-sm w-full" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Mood</label>
                        <select value={standupForm.mood}
                          onChange={e => setStandupForm(p => ({ ...p, mood: e.target.value }))}
                          className="border rounded-lg px-3 py-2 text-sm w-full">
                          <option value="">— Skip —</option>
                          {MOOD_OPTIONS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Accomplishments *</label>
                      <textarea rows={3} required value={standupForm.accomplishments}
                        onChange={e => setStandupForm(p => ({ ...p, accomplishments: e.target.value }))}
                        className="border rounded-lg px-3 py-2 text-sm w-full" placeholder="What did you accomplish today?" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Blockers</label>
                      <textarea rows={2} value={standupForm.blockers}
                        onChange={e => setStandupForm(p => ({ ...p, blockers: e.target.value }))}
                        className="border rounded-lg px-3 py-2 text-sm w-full" placeholder="Any blockers or challenges?" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Plan for Tomorrow</label>
                      <textarea rows={2} value={standupForm.plan_for_tomorrow}
                        onChange={e => setStandupForm(p => ({ ...p, plan_for_tomorrow: e.target.value }))}
                        className="border rounded-lg px-3 py-2 text-sm w-full" placeholder="What will you work on next?" />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <button type="button" onClick={resetStandupForm}
                        className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
                      <button type="submit"
                        className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                        {editingStandup ? 'Update' : 'Submit Standup'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {standups.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border p-8 text-center text-gray-500">
              No standups found for the selected period.
            </div>
          ) : (
            <div className="space-y-3">
              {standups.map(s => (
                <div key={s.id} className="bg-white rounded-xl shadow-sm border p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-700">
                        {new Date(s.date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                      </span>
                      {s.user_full_name && viewMode === 'team' && (
                        <span className="text-xs text-gray-500">— {s.user_full_name}</span>
                      )}
                      {s.mood_display && (
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{s.mood_display}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {s.reviewed_at ? (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">✓ Reviewed</span>
                      ) : (
                        <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">Pending Review</span>
                      )}
                      {!s.reviewed_at && (
                        <div className="flex gap-1">
                          <button onClick={() => handleEditStandup(s)}
                            className="text-gray-400 hover:text-blue-600 text-xs" title="Edit">✏️</button>
                          <button onClick={() => handleDeleteStandup(s.id)}
                            className="text-gray-400 hover:text-red-600 text-xs" title="Delete">🗑️</button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase">Accomplishments</p>
                      <p className="text-gray-700 whitespace-pre-wrap">{s.accomplishments}</p>
                    </div>
                    {s.blockers && (
                      <div>
                        <p className="text-xs font-medium text-red-500 uppercase">Blockers</p>
                        <p className="text-gray-700 whitespace-pre-wrap">{s.blockers}</p>
                      </div>
                    )}
                    {s.plan_for_tomorrow && (
                      <div>
                        <p className="text-xs font-medium text-blue-500 uppercase">Plan for Tomorrow</p>
                        <p className="text-gray-700 whitespace-pre-wrap">{s.plan_for_tomorrow}</p>
                      </div>
                    )}
                    {s.manager_notes && (
                      <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                        <p className="text-xs font-medium text-blue-600 uppercase">Manager Notes</p>
                        <p className="text-gray-700 whitespace-pre-wrap">{s.manager_notes}</p>
                      </div>
                    )}
                  </div>

                  {/* Manager review action */}
                  {isManager && !s.reviewed_at && (
                    <div className="mt-3 pt-3 border-t flex items-end gap-2">
                      <div className="flex-1">
                        <input type="text" placeholder="Manager notes (optional)"
                          value={reviewNotesMap[s.id] || ''}
                          onChange={e => setReviewNotesMap(p => ({ ...p, [s.id]: e.target.value }))}
                          className="border rounded-lg px-3 py-2 text-sm w-full" />
                      </div>
                      <button onClick={() => handleReviewStandup(s.id)}
                        className="px-4 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700 shrink-0">
                        Mark Reviewed
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ==================== SUMMARY TAB ==================== */}
      {activeSubTab === 'summary' && (
        <div className="space-y-4">
          {summary ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl shadow-sm border p-5">
                  <h4 className="text-sm font-medium text-gray-500 mb-3">Hours Breakdown</h4>
                  <div className="space-y-2">
                    {summary.by_category ? Object.entries(summary.by_category).map(([cat, hrs]) => (
                      <div key={cat} className="flex justify-between items-center">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[cat] || CATEGORY_COLORS.OTHER}`}>
                          {CATEGORY_OPTIONS.find(c => c.value === cat)?.label || cat}
                        </span>
                        <span className="text-sm font-semibold text-gray-700">{parseFloat(hrs).toFixed(1)}h</span>
                      </div>
                    )) : <p className="text-sm text-gray-400">No data</p>}
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-sm border p-5">
                  <h4 className="text-sm font-medium text-gray-500 mb-3">Period Stats</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Total Hours</span>
                      <span className="text-sm font-bold">{summary.total_hours?.toFixed(1) || '0.0'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Billable Hours</span>
                      <span className="text-sm font-bold text-green-600">{summary.billable_hours?.toFixed(1) || '0.0'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Days Active</span>
                      <span className="text-sm font-bold">{summary.active_days || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Avg Hours/Day</span>
                      <span className="text-sm font-bold">{summary.avg_hours_per_day?.toFixed(1) || '0.0'}</span>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-sm border p-5">
                  <h4 className="text-sm font-medium text-gray-500 mb-3">Standups</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Submitted</span>
                      <span className="text-sm font-bold">{standups.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Reviewed</span>
                      <span className="text-sm font-bold text-green-600">{standups.filter(s => s.reviewed_at).length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Pending</span>
                      <span className="text-sm font-bold text-yellow-600">{standups.filter(s => !s.reviewed_at).length}</span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border p-8 text-center text-gray-500">
              Loading summary data...
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ActivityLog;
